<template>
    <div class="qa-drawer-sidebar" :class="{ 'qa-drawer-sidebar__no-deliverable': !deliverable }">
        <div class="qa-drawer-sidebar__header">
            <h3 v-if="userCanListInternalAnnotations" class="annotations-count">
                {{ internalAnnotations.length + externalAnnotations.length }} Annotations
            </h3>
            <div class="show-resolved-control">
                <LabeledSwitch :model-value="showResolved" size="small" type="info" @change="onChange">
                    <span class="link-like">Show Resolved</span>
                </LabeledSwitch>
            </div>
        </div>
        <div v-if="deliverable" class="qa-drawer-sidebar__content">
            <hox-alert v-if="hasErrorLoadingSelectedDeliverableData" size="small" type="danger">
                <template #title>
                    "Sorry, We could not load your annotations this time. Try again in a moment."
                </template>
                <template #actionItems>
                    <Button type="primary" @click="loadSelectedDeliverableData">Load annotations</Button>
                </template>
            </hox-alert>
            <template v-else-if="isLoadingSelectedDeliverableData && isInitialLoadOfSelectedDeliverableData">
                <Spin fix />
            </template>
            <template v-else>
                <QaAnnotations
                    v-if="userCanListInternalAnnotations"
                    :annotations="internalAnnotations"
                    :annotation-type="AnnotationType.Internal"
                    :selected-annotation="selectedAnnotation"
                    :show-resolved="showResolved"
                    :users-with-status="userWithStatusByUserType.internal"
                    @annotationDeleted="removeAnnotation"
                    @annotationSelected="selectAnnotation"
                    @annotationUpdated="replaceAnnotation"
                >
                    <template #titleContent>Internal ({{ internalAnnotations.length }})</template>
                    <template #emptyMessageContent>
                        <p v-if="!internalAnnotations.length">There are currently no internal annotations.</p>
                        <p v-if="internalAnnotations.length && !showResolved">
                            There are no unresolved internal annotations.
                        </p>
                        <p v-if="$auth.userCan($auth.Actions.CanManageInternalAnnotations, { clientId, campaignId })">
                            Click anywhere on the selected banner to add one.
                        </p>
                    </template>
                </QaAnnotations>
                <QaAnnotations
                    v-if="userCanListExternalAnnotations"
                    :annotations="externalAnnotations"
                    :annotation-type="AnnotationType.External"
                    :selected-annotation="selectedAnnotation"
                    :show-resolved="showResolved"
                    :users-with-status="userWithStatusByUserType.external"
                    @annotationDeleted="removeAnnotation"
                    @annotationSelected="selectAnnotation"
                    @annotationUpdated="replaceAnnotation"
                >
                    <template #titleContent>{{ externalAnnotationsLabel }} ({{ externalAnnotations.length }})</template>
                    <template #emptyMessageContent>
                        <p>{{ externalAnnotationsEmptyMessage }}</p>
                        <p
                            v-if="
                                !$auth.userCan($auth.Actions.CanManageInternalAnnotations, { clientId, campaignId }) &&
                                $auth.userCan($auth.Actions.CanManageExternalAnnotations, { clientId, campaignId })
                            "
                        >
                            Click anywhere on the selected banner to add one.
                        </p>
                    </template>
                </QaAnnotations>
            </template>
        </div>
        <div v-else class="qa-drawer-sidebar__content">
            <div class="qa-annotations__no-annotations-message">
                <p>Select an ad to show annotations</p>
            </div>
        </div>
        <transition name="fade">
            <div
                v-if="isLoadingSelectedDeliverableData && !isInitialLoadOfSelectedDeliverableData"
                class="qa-drawer-sidebar__loading-message"
            >
                <Spin />
                Fetching updated annotations&hellip;
            </div>
        </transition>
    </div>
</template>

<script lang="ts">
import { defineComponent, type PropType } from "vue";
import type { ComputedDeliverableUserQAStatus } from "hoxton-types";

import { SuperAdminRole } from "@/enums/user";
import QaAnnotations from "@/components/Qa/QaAnnotations.vue";
import HoxAlert from "@/components/common/Alert.vue";
import { AnnotationType } from "@/enums/annotations";
import LabeledSwitch from "@/components/Campaign/LabeledSwitch.vue";
import type { ConstructedDeliverable, QaAnnotation } from "@/types/types";
import { useGA4Tracking } from "@/composables/useGA4Tracking";
import GA4 from "@/enums/ga4Events";

export default defineComponent({
    components: {
        LabeledSwitch,
        HoxAlert,
        QaAnnotations
    },

    props: {
        showResolved: {
            type: Boolean,
            default: false
        },
        deliverable: {
            type: [Object, null] as PropType<ConstructedDeliverable | null>,
            required: true
        },
        externalAnnotations: {
            type: Array as PropType<QaAnnotation[]>,
            required: true
        },
        hasErrorLoadingSelectedDeliverableData: {
            type: Boolean
        },
        internalAnnotations: {
            type: Array as PropType<QaAnnotation[]>,
            required: true
        },
        isLoadingSelectedDeliverableData: {
            type: Boolean
        },
        selectedAnnotation: {
            type: [Object, null] as PropType<QaAnnotation | null>
        }
    },

    emits: [
        "annotationDeleted",
        "annotationSelected",
        "annotationUpdated",
        "loadSelectedDeliverableData",
        "resolvedShown"
    ],

    setup() {
        const ga4 = useGA4Tracking();
        return {
            ga4
        };
    },

    data() {
        return {
            isInitialLoadOfSelectedDeliverableData: true,
            AnnotationType
        };
    },

    computed: {
        campaignId() {
            return this.$route.params.campaignId as string;
        },

        clientId() {
            return this.$route.params.clientId as string;
        },

        deliverableUserStatus() {
            const deliverableStatus =
                this.$store.state.deliverableLibrary.deliverableStatusByIdHash[this.deliverable!.idHash];

            if (deliverableStatus && deliverableStatus.users) {
                return deliverableStatus.users;
            }

            return [];
        },
        externalAnnotationsEmptyMessage() {
            return this.userCanListExternalAnnotations && !this.userCanListInternalAnnotations
                ? "No annotations."
                : "No external annotations.";
        },
        externalAnnotationsLabel() {
            return this.userCanListExternalAnnotations && !this.userCanListInternalAnnotations
                ? "Annotations"
                : "External";
        },
        userCanListExternalAnnotations() {
            return this.$auth.userCan(this.$auth.Actions.CanListExternalAnnotations);
        },
        userCanListInternalAnnotations() {
            return this.$auth.userCan(this.$auth.Actions.CanListInternalAnnotations);
        },
        userWithStatusByUserType() {
            return this.deliverableUserStatus.reduce(
                (userWithStatusByUserType, userStatus) => {
                    let userType: "external" | "internal" = "external";

                    if (
                        userStatus.user.role.name === SuperAdminRole ||
                        userStatus.user.role.actions?.includes(this.$auth.Actions.CanManageInternalAnnotations)
                    ) {
                        userType = "internal";
                    }

                    userWithStatusByUserType[userType].push(userStatus);

                    return userWithStatusByUserType;
                },
                {
                    external: [],
                    internal: []
                } as Record<"external" | "internal", ComputedDeliverableUserQAStatus[]>
            );
        }
    },
    watch: {
        deliverable() {
            this.isInitialLoadOfSelectedDeliverableData = true;
        },
        isLoadingSelectedDeliverableData() {
            if (this.hasErrorLoadingSelectedDeliverableData) {
                this.isInitialLoadOfSelectedDeliverableData = true;
            } else if (!this.isLoadingSelectedDeliverableData) {
                this.isInitialLoadOfSelectedDeliverableData = false;
            }
        }
    },

    methods: {
        loadSelectedDeliverableData() {
            this.$emit("loadSelectedDeliverableData");
        },

        onChange(val: boolean) {
            this.ga4.trackClickEvent({
                interactionObject: GA4.CLICK_INTERACTION_OBJECTS.CHECKBOX,
                interactionText: GA4.CLICK_INTERACTION_TEXTS.SHOW_RESOLVED
            });
            this.$emit("resolvedShown", val);
        },

        removeAnnotation(annotation: QaAnnotation) {
            this.$emit("annotationDeleted", annotation);
        },

        replaceAnnotation(annotation: QaAnnotation) {
            this.$emit("annotationUpdated", annotation);
        },

        selectAnnotation(annotation: QaAnnotation) {
            this.$emit("annotationSelected", annotation);
        }
    }
});
</script>

<style lang="scss">
@use "@style/variables" as *;

.qa-drawer-sidebar {
    display: flex;
    flex-direction: column;
    overflow-y: auto;
    padding: $spacing 0 0 0;
    height: 100%;
    width: 100%;
}

.qa-drawer-sidebar__no-deliverable {
    .qa-annotations__no-annotations-message {
        margin: 8px;
    }
}

.qa-drawer-sidebar__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0 20px 10px;

    .ivu-switch {
        margin-left: 5px;
    }

    .show-resolved-control {
        margin-left: auto;
    }
}

.qa-drawer-sidebar__content {
    flex: 1;
    position: relative;
}

.qa-drawer-sidebar__loading-message {
    background: $white;
    bottom: $spacing-small;
    box-shadow: 0 1px 16px rgba(0, 0, 0, 0.2);
    font-size: $font-size-small;
    left: $spacing-small;
    padding: $spacing;
    position: absolute;
    right: $spacing-small;

    .ivu-spin {
        display: inline-block;
    }
}

.qa-drawer-sidebar__status-container {
    border-bottom: 1px solid $grey3;
    padding: 0 $spacing $spacing;
}

.qa-drawer-sidebar__title {
    font-size: $font-size-larger;
    margin: 0 $spacing $spacing-smaller;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
</style>
